/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by richardwilliams on 4/9/15.
 */

function PackageMediaViewController (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    $routeParams: JS5Object,
    $timeout: JS5Function,
    AsperaConnectService: JS5Object,
    AssetValidationService: JS5Object,
    ContentResourceFactory: JS5Object,
    DynamicPropertiesService: JS5Object,
    LanguageResourceFactory: JS5Object,
    MediaProfileResourceFactory: JS5Object,
    ModalService: JS5Object,
    NotificationService: JS5Object,
    PackageFileUploadService: JS5Object,
    PackageMediaResourceFactory: JS5Object,
    PackageMediaService: JS5Object,
    PackageResourceFactory: JS5Object,
    PermissionsService: JS5Object,
    PackageUtilService: JS5Object,
) {
    this.getMainVideo = (): JS5Object => {
        return PackageMediaService.getMainVideo();
    };

    this.showFileProgressModal = () => {
        ModalService.showFileProgressModal();
        console.log('hi');
    };

    //================//
    // Template Logic //
    //================//

    this.isComponentRoleRequired = (componentRole: JS5Object) => {
        return componentRole.isDefault || !!_.size(componentRole._partnersRequiring);
    };

    this.isComponentRoleEnabled = (componentRole: JS5Object) => {
        return componentRole._contentCategoryEnabled || componentRole.isDefault;
    };

    this.getComponentRoleCount = () => {
        return _.filter(this.componentRoles, (componentRole: JS5Object) => {
            return this.isComponentRoleEnabled(componentRole);
        }).length;
    };

    const getAllVideos = () => {
        let allRoleVideos = _.filter(PackageMediaService.packageMediaData.assets, {
            type: 'VideoComponentMaster'
        });
        return _.concat(allRoleVideos, [this.getMainVideo()]);
    };

    const checkVideosForRunningWorkflow = (videos: JS5Object[], workflowPropertyName: string) => {
        let foundWorkflow = false;

        _.forEach(videos, (video: JS5Object) => {
            foundWorkflow = foundWorkflow || !!_.get(video, `ingestInfo.${workflowPropertyName}`);
        });

        return foundWorkflow;
    };

    const isWorkflowInProgress = (workflowIdProperty: string) => {
        let videos: JS5Object[] = [];
        if (this.activeTabRole.isDefault) {
            videos = getAllVideos();
        } else {
            const roleVideo = _.find(PackageMediaService.packageMediaData.assets, {
                type: 'VideoComponentMaster',
                componentRole: {
                    id: this.activeTabRole.id
                }
            });

            if (roleVideo) {
                videos.push(roleVideo);
            }
        }

        return checkVideosForRunningWorkflow(videos, workflowIdProperty);
    };

    this.getLockMessage = () => {
        if (isWorkflowInProgress('workflowId')) {
            return `Ingest in progress`;
        } else if (isWorkflowInProgress('proxyWorkflowId')) {
            return `Proxy creation in progress`;
        } else {
            return ``;
        }
    };

    this.wasMainVideoUploaded = () => {
        return PackageMediaService.isAnUploadPath();
    };

    this.getDefaultComponentRole = () => {
        return _.find(PackageMediaService.packageMediaData.componentRoles, {isDefault: true});
    };

    this.onTabSelect = (componentRole: JS5Object) => {
        this.activeTabRole = componentRole;
    };

    this.onTabDeselect = (componentRole: JS5Object, $event: JS5Object) => {
        if (_.get(this, `packageMediaEditorForm.$dirty`, false)) {
            $event.preventDefault();
            NotificationService.createWarningNotification(
                'Please save or revert your changes before editing another component role.');
        }
    };

    this.onFindRelated = () => {
        this.loadingIndicator.show = true;
        PackageMediaService.findRelatedItems().finally(() => {
            this.loadingIndicator.show = false;
        });
        this.packageMediaEditorForm.$setDirty();
    };


    //=========//
    // Toolbar //
    //=========//

    const savePackageMediaData = () => {
        this.loadingIndicator.show = true;
        PackageFileUploadService.uploadFiles(this.packageObjectData, this.loadingIndicator)
            .then(() => PackageMediaService.savePackageMediaData())
            .then(() => {
                this.packageMediaEditorForm.$setPristine();
            })
            .finally(() => {
                this.loadingIndicator.show = false;
            });
    };

    const hasRedundantAudioLanguage = () => {
        const audioAssets = PackageMediaService.getAssetList('Audio');
        let hasConflict = false;

        _.forEach(audioAssets, (asset: JS5Object) => {
            const isSpokenLanguage = asset.role.name === 'Spoken Language';
            const languageMatchesMainVideo =
                _.get(PackageMediaService.getMainVideo(), 'language.id') === _.get(asset, 'language.id');
            hasConflict = hasConflict || (isSpokenLanguage && languageMatchesMainVideo);
        });

        return hasConflict;
    };

    this.onSaveClicked = () => {
        if (!hasRedundantAudioLanguage()) {
            const clearPendingIngestAndSave = () => {
                PackageMediaService.packageMediaData.pendingIngest = false;
                savePackageMediaData();
            };
            const setPendingIngestAndSave = () => {
                PackageMediaService.packageMediaData.pendingIngest = true;
                savePackageMediaData();
            };

            if (this.getComponentRoleCount() > 1) {
                ModalService.showMessageModal(
                    {
                        title: 'Save Package Media',
                        pullCancelLeft: true,
                        messageText: `Have all component files been ingested?`,
                        windowClass: 'confirm-modal-sm text-align-center',
                        okText: 'Yes',
                        cancelText: 'No',
                        showOK: true
                    },
                    clearPendingIngestAndSave,
                    setPendingIngestAndSave);
            } else {
                // Save without prompt when there's no active component
                // roles for this package
                clearPendingIngestAndSave();
            }
        } else {
            ModalService.showMessageModal({
                messageText: `Language for audio asset with role "Spoken Language" conflicts with main video language`,
                pullCancelLeft: true,
                title: 'Audio Role Language Conflict',
                windowClass: 'confirm-modal-lg',
                showOK: true
            });
        }
    };

    this.onRevertClicked = (originalPackageObjectData: JS5Object) => {
        originalPackageObjectData.primaryAcceptTemplateMember =
            PackageMediaService.originalPackageMediaData.selectedVideoAcceptTemplateMember;
        PackageMediaService.revertPackageMediaData();

        this.packageObjectData = originalPackageObjectData;
        PackageMediaService.getPackageMediaData(this.packageObjectData);
    };


    //=================//
    // Lifecycle Hooks //
    //=================//

    const setupComponentTabs = (componentRoles: JS5Object[]) => {
        let defaultRole: JS5Object = {};
        let requiredRoles: JS5Object[] = [];
        let optionalRoles: JS5Object[] = [];

        // order roles by 1) default, 2) required, 3) optional
        _.forEach(componentRoles, (componentRole: JS5Object) => {
            if (componentRole.isDefault) {
                defaultRole = componentRole;
            } else if (_.size(componentRole._partnersRequiring)) {
                requiredRoles.push(componentRole);
            } else {
                optionalRoles.push(componentRole);
            }
            this.activeTabs[componentRole.name] = componentRole.isDefault;
        });
        this.componentRoles = _.flatten([defaultRole, requiredRoles, optionalRoles]);

        this.activeTabs[defaultRole.name] = true;
        this.activeTabRole = _.find(componentRoles, {isDefault: true});
    };

    const getData = () => {
        return PackageMediaService.getPackageMediaData(this.packageObjectData).then((packageMediaData: JS5Object) => {
            return packageMediaData;
        });
    };

    const initRemoteUpload = () => {
        AsperaConnectService.initialize()
            .then(() => {
                console.info(`PackageMediaView.initRemoteUpload(): init succeeded`);
            })
            .catch((minAsperaConnectVersion: string) => {
                console.info(`PackageMediaView.initRemoteUpload(): init failed`);
                ModalService.showMessageModal(
                    {
                        messageText:
                            `The AsperaConnect browser plugin failed to launch in time.` +
                            ` Would you like to try again?`,
                        title: `Re-launch AsperaConnect Plugin`,
                        windowClass: 'confirm-modal-sm',
                        showOK: true,
                        okText: 'Yes',
                        pullCancelLeft: true
                    },

                    // accept handler
                    () => {
                        console.info(`PackageMediaView.initRemoteUpload(): trying again`);
                        initRemoteUpload();
                    },

                    // reject handler
                    () => {
                        AsperaConnectService.reset();
                        console.info(`PackageMediaView.initRemoteUpload(): user declined to try again`);
                        NotificationService.createFailureNotification(
                            `AsperaConnect failed to launch.`,
                            `AsperaConnect could not be installed and/or launched. Asset upload will be disabled.` +
                            ` Please verify that AsperaConnect` +
                            ` ${minAsperaConnectVersion} is installed and working` +
                            ` properly and refresh the tab to try again.`,
                            true,
                            -1);
                    }
                );
            });
    };

    this.$onInit = () => {
        this.loadingIndicator.show = true;

        this.activeTabs = {};

        PackageMediaService.getPackageMediaPermissions().then((permissions: JS5Object) => {
            this.isAdmin = permissions.isAdmin;
            this.canRead = permissions.canRead;
            this.canWrite = permissions.canWrite;

            if (this.canRead) {
                getData().then((packageMediaData: JS5Object) => {
                    initRemoteUpload();
                    setupComponentTabs(packageMediaData.componentRoles);

                    PackageUtilService.tabSaveFunction = this.onSaveClicked;
                    PackageUtilService.tabRevertFunction = this.onRevertClicked;

                    this.loadingIndicator.show = false;

                    PackageMediaService.activateIngestProgressPollingInterval();
                });
            } else {
                this.loadingIndicator.show = false;
            }
        });

        PackageUtilService.showHoldForComponentsCB = () => {
            return PackageMediaService.packageMediaData.pendingIngest && this.getComponentRoleCount() > 1;
        }
    };

    this.$onDestroy = () => {
        PackageMediaService.deactivateIngestProgressPollingInterval();
        PackageMediaService.resetPackageMediaData(null);
        PackageFileUploadService.unregisterAllFiles();
        AsperaConnectService.reset();
    };

    this.$postLink = () => {
        $timeout(() => {
            PackageUtilService.editorForm = this.packageMediaEditorForm;
        });
    };
}

angular.module('prism-ui.package').component('packageMediaView', {
    controller: PackageMediaViewController,
    templateUrl: 'package/editor/components/view/media/packagemedia-view-template.html',
    bindings: {
        loadingIndicator: '=',
        packageObjectData: '=',
        pkgEditCtrl: '='
    }
});
